import { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { TopBar, type NavId } from './components/TopBar';
import { Auth } from './features/auth/Auth';
import { Dashboard } from './features/dashboard/Dashboard';
import { Portfolio } from './features/portfolio/Portfolio';
import { Wallet } from './features/wallet/Wallet';
import { Watchlist } from './features/watchlist/Watchlist';
import { StockDetail } from './features/stock/StockDetail';
import { Bots } from './features/bots/Bots';
import { BotDetails } from './features/bots/BotDetails';
import { TKTweaks, initialTweaks } from './tweaks/Tweaks';
import { type TweakSettings } from './tweaks/types';
import { AuthProvider, useAuth } from './lib/auth';

const THEME_KEY = 'tk-theme';

type Theme = 'light' | 'dark';

const useTheme = () => {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === 'dark' ? 'dark' : 'light';
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);
  return [theme, setTheme] as const;
};

interface ShellProps {
  theme: Theme;
  setTheme: (t: Theme) => void;
  tweaks: TweakSettings;
}

const Shell = ({ theme, setTheme, tweaks }: ShellProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { signout } = useAuth();

  const handleNav = (id: NavId) => {
    if (id === 'markets') navigate('/stock/ENGROH');
    else if (id === 'portfolio') navigate('/portfolio');
    else if (id === 'wallet') navigate('/wallet');
    else if (id === 'watchlist') navigate('/watchlist');
    else if (id === 'bots') navigate('/bots');
    else navigate('/');
  };

  const currentNav: NavId | string = pathname.startsWith('/stock')
    ? 'markets'
    : pathname.startsWith('/portfolio')
      ? 'portfolio'
      : pathname.startsWith('/wallet')
        ? 'wallet'
        : pathname.startsWith('/watchlist')
          ? 'watchlist'
            : pathname.startsWith('/bots')
              ? 'bots'
              : 'dashboard';

  return (
    <>
      <TopBar
        theme={theme}
        onToggleTheme={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        currentNav={currentNav}
        onNav={handleNav}
        showTickerTape={tweaks.showTickerTape !== false}
        onSignOut={async () => {
          await signout();
          navigate('/auth/signin', { replace: true });
        }}
      />
      <main>
        <Outlet />
      </main>
      <footer
        style={{
          maxWidth: 1440,
          margin: '0 auto',
          padding: '24px 32px 48px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'var(--tk-ink-3)',
          fontSize: 12,
          borderTop: '1px solid var(--tk-line-2)',
        }}
      >
        <span>© 2026 TradeKaro · Securities & Exchange Commission of Pakistan regulated</span>
        <span style={{ fontFamily: 'var(--tk-mono)' }}>v0.5 · live data</span>
      </footer>
    </>
  );
};

const StockRoute = () => {
  const navigate = useNavigate();
  const params = useParams();
  return (
    <StockDetail
      symbol={params.sym ?? 'ENGROH'}
      onBack={() => navigate('/')}
      onChangeSymbol={(sym) => navigate(`/stock/${sym}`)}
    />
  );
};

const DashboardRoute = ({ tweaks }: { tweaks: TweakSettings }) => {
  const navigate = useNavigate();
  return <Dashboard tweaks={tweaks} onPickStock={(sym) => navigate(`/stock/${sym}`)} />;
};

const PortfolioRoute = () => {
  const navigate = useNavigate();
  return <Portfolio onPickStock={(sym) => navigate(`/stock/${sym}`)} />;
};

const BotsRoute = () => {
  const navigate = useNavigate();
  return <Bots onOpen={(id) => navigate(`/bots/${id}`)} />;
};

const BotDetailsRoute = () => {
  const navigate = useNavigate();
  const params = useParams();
  return <BotDetails key={params.id} id={params.id ?? ''} onBack={() => navigate('/bots')} onOpenSymbol={(sym) => navigate(`/stock/${sym}`)} />;
};

const WatchlistRoute = () => {
  const navigate = useNavigate();
  return <Watchlist onPickStock={(sym) => navigate(`/stock/${sym}`)} />;
};

interface AppProps {
  defaultAuthMode: 'signin' | 'signup';
}

const Routed = ({ defaultAuthMode }: AppProps) => {
  const [theme, setTheme] = useTheme();
  const [tweaks, setTweaks] = useState<TweakSettings>(initialTweaks);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const authed = !!user;

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--tk-ink-3)',
          fontFamily: 'var(--tk-mono)',
          fontSize: 13,
        }}
      >
        Loading…
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route
          path="/auth"
          element={
            authed ? (
              <Navigate to="/" replace />
            ) : (
              <Auth mode={defaultAuthMode} onComplete={() => navigate('/', { replace: true })} />
            )
          }
        />
        <Route
          path="/auth/signin"
          element={
            authed ? (
              <Navigate to="/" replace />
            ) : (
              <Auth mode="signin" onComplete={() => navigate('/', { replace: true })} />
            )
          }
        />
        <Route
          path="/auth/signup"
          element={
            authed ? (
              <Navigate to="/" replace />
            ) : (
              <Auth mode="signup" onComplete={() => navigate('/', { replace: true })} />
            )
          }
        />

        <Route
          element={
            authed ? (
              <Shell theme={theme} setTheme={setTheme} tweaks={tweaks} />
            ) : (
              <Navigate to="/auth/signin" replace />
            )
          }
        >
          <Route path="/" element={<DashboardRoute tweaks={tweaks} />} />
          <Route path="/portfolio" element={<PortfolioRoute />} />
          <Route path="/watchlist" element={<WatchlistRoute />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/stock/:sym" element={<StockRoute />} />
          <Route path="/bots" element={<BotsRoute />} />
          <Route path="/bots/:id" element={<BotDetailsRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      {authed && <TKTweaks tweaks={tweaks} onChange={setTweaks} theme={theme} />}
    </>
  );
};

export const App = (props: AppProps) => (
  <AuthProvider>
    <Routed {...props} />
  </AuthProvider>
);
